import type {
  CustomInheritBehavior,
  SiteFooterSettings,
  SiteHeaderSettings,
  SiteThemeSettings,
} from '@kitconcept/volto-light-theme/src/types';
import { juntsCardBlock } from './components/CardBlock';
import { juntsHeadingBlock } from './components/HeadingBlock';
import HelpBot from './components/HelpBot/HelpBot';
import { heroHeadingBlock } from './components/HeroHeadingBlock';
import { iframeBlock } from './components/IframeBlock';
import { JuntsFileVariant } from './components/Listing/JuntsFileVariant/JuntsFileVariant';
import { JuntsGridNewsVariant } from './components/Listing/JuntsGridNewsVariant/JuntsGridNews';
import LoginCode from './components/LoginCode/LoginCode';
import Popup from './components/Popup/Popup';
import { juntsTwitterBlock } from './components/TwitterBlock';
import installAlignTextPlugin from './editor/plugins/AlignTextPlugin';
import installTooltipPlugin from './editor/plugins/TooltipPlugin';
import { TOOLTIP } from './editor/plugins/TooltipPlugin/constants';
import userSession from './reducers/userSession';

declare module '@plone/types' {
  export interface Content {
    footer_logos_container_width: string;
    footer_logos_size: string;
  }

  export interface Expanders {
    inherit: {
      'voltolighttheme.header': CustomInheritBehavior<SiteHeaderSettings>;
      'voltolighttheme.theme': CustomInheritBehavior<SiteThemeSettings>;
      'voltolighttheme.footer': CustomInheritBehavior<SiteFooterSettings>;
    };
  }
  export interface BlocksConfigData {
    junts_heading: {
      show_alignment: boolean;
      default_tag: string;
      allowed_headings: string[];
    };
    junts_twitter: {
      twitter_username: string;
    };
  }
}

const applyConfig = (config) => {
  config.settings.isMultilingual = false;
  config.settings.supportedLanguages = ['en'];
  config.settings.defaultLanguage = 'en';

  config.settings.slate.toolbarButtons = [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'separator',
    'heading-two',
    'heading-three',
    'heading-four',
    'separator',
    'clearformatting',
    'separator',
    'sub',
    'sup',
    'separator',
    'bulleted-list',
    'numbered-list',
    'blockquote',
    'styleMenu',
    'separator',
    'code',
    'link',
    TOOLTIP,
  ];
  config.settings.slate.expandedToolbarButtons = [
    ...(config.settings.slate.expandedToolbarButtons || []),
    'link',
    TOOLTIP,
  ];
  config = installAlignTextPlugin(installTooltipPlugin(config));

  config.blocks.blocksConfig['junts_heading'] = juntsHeadingBlock;
  config.blocks.blocksConfig['junts_twitter'] = juntsTwitterBlock;
  config.blocks.blocksConfig['junts_cards'] = juntsCardBlock;
  config.blocks.blocksConfig['junts_iframe'] = iframeBlock;
  config.blocks.blocksConfig['junts_hero_heading'] = heroHeadingBlock;

  config.blocks.blocksConfig.listing.variations = [
    ...config.blocks.blocksConfig.listing.variations,
    {
      id: 'juntsFileVariant',
      title: 'Junts file variant',
      template: JuntsFileVariant,
      fullobjects: true,
    },
    {
      id: 'juntsGridNewsVariant',
      title: 'Junts grid news variant',
      template: JuntsGridNewsVariant,
    },
  ];

  // TODO: full calendar no carrega com a possible bloc dins del grid
  config.blocks.blocksConfig.gridBlock = {
    ...config.blocks.blocksConfig.gridBlock,
    title: 'Grid',
    allowedBlocks: [
      'image',
      'listing',
      'slate',
      'teaser',
      'fullcalendar',
      'junts_heading',
      'junts_cards',
    ],
    blocksConfig: config.blocks.blocksConfig,
  };
  // Palettes
  config.blocks.themes = [
    {
      style: {
        '--theme-color': '#fff', // Block Wrapper
        '--theme-high-contrast-color': '#ecebeb', // Cards in Grid block
        '--theme-foreground-color': 'var(--junts-dark-green-color)',
        '--theme-low-contrast-foreground-color':
          'var(--junts-dark-green-color)',
      },
      name: 'default',
      label: 'Default',
    },
    {
      style: {
        '--theme-color': '#ecebeb',
        '--theme-high-contrast-color': '#fff',
        '--theme-foreground-color': 'var(--junts-dark-green-color)',
        '--theme-low-contrast-foreground-color':
          'var(--junts-dark-green-color)',
      },
      name: 'grey',
      label: 'Grey',
    },
  ];
  config.addonRoutes.push({
    path: ['/login-code', '/**/login-code'],
    component: LoginCode,
  });
  config.settings.nonContentRoutes.push('/login-code', '/register');
  config.addonReducers = {
    ...config.addonReducers,
    userSession: userSession,
  };

  config.settings.supportedLanguages = ['en', 'es', 'ca'];
  config.settings.defaultLanguage = 'ca';
  config.settings.DSGVOBanner = {
    ...(config.settings.DSGVOBanner || {}),
    modules: ['tracking'],
  };
  config.settings.navDepth = 2;
  config.settings.DSGVOBanner.tracker.id = 'G-RZGJ8QWQSZ';
  config.settings.appExtras = [
    ...(config.settings.appExtras || []),
    {
      match: '',
      component: HelpBot,
    },
    {
      match: '',
      component: Popup,
    },
  ];
  return config;
};

export default applyConfig;
